import React from 'react';
import type { Position } from '../../lib/types';
import { formatCurrency, formatDateDDMMYYYY } from './formatters';

interface CurrentPositionsSectionProps {
  positions: Array<Position & { userName: string }>;
}

const PositionRow: React.FC<{ position: Position & { userName: string } }> = ({ position }) => {
  const size = Number.parseFloat(position.size);
  if (size <= 0) {
    return null;
  }

  const markPrice = Number.parseFloat(position.markPrice);
  const takeProfitPrice = Number.parseFloat(position.takeProfit || '0');
  const hasTakeProfit = takeProfitPrice > 0;
  const tpDiff = hasTakeProfit
    ? position.side === 'Buy'
      ? takeProfitPrice - markPrice
      : markPrice - takeProfitPrice
    : null;
  const tpDistancePercent = tpDiff !== null && markPrice > 0 ? (tpDiff / markPrice) * 100 : null;
  const unrealisedPnl = Number.parseFloat(position.unrealisedPnl || '0');
  const isProfit = unrealisedPnl > 0;

  return (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-900">{position.userName}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{position.symbol}</td>
      <td className="px-4 py-3 text-sm">
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            position.side === 'Buy' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {position.side}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{size.toFixed(4)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(Number.parseFloat(position.avgPrice))}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(markPrice)}</td>
      <td className="px-4 py-3 text-sm text-gray-900">{hasTakeProfit ? formatCurrency(takeProfitPrice) : '-'}</td>
      <td className="px-4 py-3 text-sm text-gray-900">
        {tpDistancePercent !== null ? `${tpDistancePercent >= 0 ? '+' : ''}${tpDistancePercent.toFixed(2)}%` : '-'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{position.leverage}x</td>
      <td className="px-4 py-3 text-sm">
        <span className={`font-semibold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(unrealisedPnl)}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{formatDateDDMMYYYY(position.createdTime)}</td>
    </tr>
  );
};

export const CurrentPositionsSection: React.FC<CurrentPositionsSectionProps> = ({ positions }) => {
  if (positions.length === 0) {
    return null;
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Current Open Positions</h2>
        <p className="text-sm text-gray-600 mt-1">Active positions across all traders</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trader</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Symbol</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Side</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Size</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entry Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mark Price</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Take Profit</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Distance to TP</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leverage</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unrealized P&L (USDT)</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opened (UTC)</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {positions.map((position, index) => (
              <PositionRow key={`${position.symbol}-${index}`} position={position} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
